import { ChevronRight, Zap } from "lucide-react";

/** Strip markdown syntax and filter AI error/refusal messages. Returns null to hide. */
function cleanPulse(text) {
  if (!text || !text.trim()) return null;
  // Hide common AI refusal/error patterns
  if (/^(i'?m sorry|i cannot|as an ai|i don'?t have|unfortunately[,\s]i|my knowledge is|i apologize)/i.test(text.trim())) return null;
  return text
    .replace(/^#{1,3}\s*/gm, "")       // Strip ## / ### heading markers
    .replace(/\*\*(.*?)\*\*/g, "$1")    // Strip **bold**
    .replace(/\*(.*?)\*/g, "$1")        // Strip *italic*
    .replace(/\n{3,}/g, "\n\n")         // Collapse excessive blank lines
    .trim();
}

export default function PartyCard({ party, onClick, pulse, pulseLoading }) {
  return (
    <div
      data-testid={`party-card-${party.id}`}
      className="bg-white border-2 border-black cursor-pointer transition-transform party-card-hover flex flex-col"
      style={{ boxShadow: "4px 4px 0px 0px #000000" }}
      onClick={onClick}
    >
      {/* Color accent strip */}
      <div className="h-2 w-full" style={{ backgroundColor: party.color }} />

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-3">
          <span
            className="text-3xl font-black tracking-tighter leading-none"
            style={{ fontFamily: "Outfit, sans-serif", color: party.color }}
          >
            {party.abbreviation}
          </span>
          <span
            className="text-xs px-2 py-1 border border-black font-semibold"
            style={{ backgroundColor: "#F9F7F2", color: "#475569" }}
          >
            {party.ideology}
          </span>
        </div>

        <h3
          className="font-black text-sm mb-1 leading-snug"
          style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}
        >
          {party.name}
        </h3>

        <p className="text-xs text-[#475569] mb-1">
          <span className="font-semibold">Leader:</span> {party.leader}
        </p>

        {party.founded && (
          <p className="text-xs text-[#475569] mb-3">
            <span className="font-semibold">Founded:</span> {party.founded}
          </p>
        )}

        <p className="text-xs text-[#475569] mb-3 line-clamp-2">{party.description}</p>

        {/* On The Pulse — live AI snippet */}
        <div
          className="mb-4 p-2.5 border border-black"
          style={{ backgroundColor: `${party.color}0D` }}
          data-testid={`pulse-${party.id}`}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#10B981", boxShadow: "0 0 4px #10B981" }}
            />
            <span className="text-xs font-black tracking-wide uppercase" style={{ color: party.color }}>
              On The Pulse
            </span>
            <Zap className="w-3 h-3 ml-auto" style={{ color: party.color, opacity: 0.6 }} />
          </div>
          {pulseLoading ? (
            <div className="space-y-1.5">
              <div className="h-2.5 rounded animate-pulse" style={{ backgroundColor: `${party.color}30`, width: "90%" }} />
              <div className="h-2.5 rounded animate-pulse" style={{ backgroundColor: `${party.color}20`, width: "70%" }} />
            </div>
          ) : cleanPulse(pulse) ? (
            <p className="text-xs leading-relaxed whitespace-pre-line" style={{ color: "#334155" }}>{cleanPulse(pulse)}</p>
          ) : (
            <p className="text-xs italic" style={{ color: "#94A3B8" }}>AI summary unavailable</p>
          )}
        </div>

        {/* Policy tags */}
        <div className="flex flex-wrap gap-1 mb-4">
          {party.positions.slice(0, 3).map((pos, i) => (
            <span
              key={i}
              className="text-xs px-2 py-0.5 border border-black font-medium"
              style={{ backgroundColor: `${party.color}18`, color: party.color }}
            >
              {pos}
            </span>
          ))}
        </div>

        <button
          data-testid={`learn-more-${party.id}`}
          className="mt-auto w-full py-2 text-white font-bold text-sm border-2 border-black hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          style={{ backgroundColor: party.color, boxShadow: "2px 2px 0px 0px #000000" }}
          onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
          Learn More <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
